/**
 * AdminPayoutsPage — manual-release queue for expert payouts.
 *
 * Two tabs:
 *   • Pending   — bookings where the expert is owed money but no payout
 *                 has been initiated yet. Per-row "Release" button + bulk.
 *                 Below it, async reviews owed a reviewer payout (80% of
 *                 price_charged), released one at a time.
 *   • History   — past payouts with status (in_transit/paid/failed).
 *
 * "Pending" is computed on the fly (no DB queue table) — see
 * backend/routes/admin_payouts.py:list_pending_payouts.
 *
 * Rows where the expert hasn't completed Stripe Connect onboarding are
 * surfaced with a "Expert not onboarded" badge and the Release button
 * disabled, so the founder sees the booking exists but understands why
 * it can't be released yet.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, CheckCircle2, AlertTriangle, Loader2,
  Send, History, Wallet, ExternalLink, Undo2, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getAdminToken } from "@/lib/authStorage";
import { API } from "@/App";
import { toast } from "sonner";


const TABS = [
  { id: "pending", label: "Pending",  icon: Send,    desc: "Bookings and reviews owed payout" },
  { id: "history", label: "History",  icon: History, desc: "Past releases" },
];


function authHeaders() {
  const tok = getAdminToken();
  return { Authorization: tok ? `Bearer ${tok}` : "", "Content-Type": "application/json" };
}


async function fetchJson(url, opts = {}) {
  const res = await fetch(url, opts);
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try { detail = (await res.clone().text()) || detail; } catch (_) { /* fallthrough */ }
    throw new Error(detail);
  }
  return res.clone().json();
}


function fmtUSD(v) {
  if (v === null || v === undefined) return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (Number.isNaN(n)) return v;
  return `$${n.toFixed(2)}`;
}


function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch (_) {
    return iso;
  }
}


function StatusBadge({ status }) {
  const styles = {
    in_transit: "bg-amber-100 text-amber-800 border-amber-200",
    paid:       "bg-brand-green/10 text-brand-green border-brand-green/20",
    failed:     "bg-red-100 text-red-800 border-red-200",
    pending:    "bg-warm-grey-100 text-warm-grey-700 border-warm-grey-200",
    cancelled:  "bg-warm-grey-100 text-warm-grey-700 border-warm-grey-200",
    reversed:   "bg-purple-100 text-purple-800 border-purple-200",
  };
  const cls = styles[status] || styles.pending;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${cls}`}>
      {status}
    </span>
  );
}


// ─── Tab: Pending ──────────────────────────────────────────────────────────


function PendingTab() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ pending: [], count: 0, feature_enabled: false });
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());     // booking_ids
  const [releasing, setReleasing] = useState(new Set());   // booking_ids currently in flight

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchJson(`${API}/admin/payouts/pending`, { headers: authHeaders() });
      setData(d);
      setSelected(new Set());
    } catch (e) {
      setError(e.message || "Failed to load pending payouts.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const releasableIds = useMemo(
    () => (data.pending || []).filter((r) => r.connect_ready).map((r) => r.booking_id),
    [data.pending],
  );

  const totalOwed = useMemo(
    () => (data.pending || []).reduce((s, r) => s + parseFloat(r.amount_payout_usd || 0), 0),
    [data.pending],
  );

  const releaseOne = async (booking_id) => {
    setReleasing((prev) => new Set(prev).add(booking_id));
    try {
      await fetchJson(`${API}/admin/payouts/${booking_id}/release`, {
        method: "POST",
        headers: authHeaders(),
      });
      toast.success("Payout released. Status will update via Stripe webhook.");
      await load();
    } catch (e) {
      toast.error(`Release failed: ${e.message}`);
      setReleasing((prev) => {
        const n = new Set(prev);
        n.delete(booking_id);
        return n;
      });
    }
  };

  const releaseBulk = async () => {
    if (selected.size === 0) return;
    const booking_ids = Array.from(selected);
    setReleasing((prev) => {
      const n = new Set(prev);
      booking_ids.forEach((id) => n.add(id));
      return n;
    });
    try {
      const res = await fetchJson(`${API}/admin/payouts/release-bulk`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ booking_ids }),
      });
      if (res.failed_count > 0) {
        toast.warning(
          `Released ${res.released_count}/${booking_ids.length} — ${res.failed_count} failed (check console).`,
        );
        // eslint-disable-next-line no-console
        console.log("Bulk release results", res.results);
      } else {
        toast.success(`Released ${res.released_count} payouts.`);
      }
      await load();
    } catch (e) {
      toast.error(`Bulk release failed: ${e.message}`);
    } finally {
      setReleasing(new Set());
    }
  };

  const toggleOne = (booking_id, releasable) => {
    if (!releasable) return;
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(booking_id) ? next.delete(booking_id) : next.add(booking_id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === releasableIds.length) setSelected(new Set());
    else setSelected(new Set(releasableIds));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-brand-green" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-red-200 rounded p-6">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <p className="font-semibold text-red-800">Couldn't load pending payouts</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <Button onClick={load} variant="ghost" className="mt-3 -ml-3">
              <RefreshCw className="w-4 h-4 mr-1" /> Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {!data.feature_enabled && (
        <div className="bg-amber-50 border border-amber-200 rounded p-4 mb-4 text-sm text-amber-900">
          <strong>STRIPE_CONNECT_ENABLED=false</strong> — release buttons will return 503. Flip the feature flag in Fly secrets to release real money.
        </div>
      )}

      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div>
          <div className="font-display text-2xl">{data.count} pending</div>
          <div className="text-sm text-warm-grey-700">
            Total owed: <strong>{fmtUSD(totalOwed)}</strong>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="ghost" size="sm">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button
            onClick={releaseBulk}
            disabled={selected.size === 0 || releasing.size > 0}
            className="bg-brand-green hover:bg-brand-green-dark text-white rounded-md"
          >
            Release {selected.size} selected
          </Button>
        </div>
      </div>

      {data.count === 0 ? (
        <div className="bg-white border border-warm-grey-200 rounded p-8 text-center text-warm-grey-700">
          <CheckCircle2 className="w-8 h-8 text-brand-green mx-auto mb-2" />
          No payouts pending — every paid booking has been released.
        </div>
      ) : (
        <div className="bg-white border border-warm-grey-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-paper border-b border-warm-grey-200">
              <tr>
                <th className="w-8 p-3">
                  <input
                    type="checkbox"
                    checked={releasableIds.length > 0 && selected.size === releasableIds.length}
                    onChange={toggleAll}
                  />
                </th>
                <th className="text-left p-3">Booking</th>
                <th className="text-left p-3">Expert</th>
                <th className="text-left p-3">Seeker</th>
                <th className="text-right p-3">Total</th>
                <th className="text-right p-3">Payout</th>
                <th className="text-right p-3">Fee</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.pending.map((r) => {
                const isReleasing = releasing.has(r.booking_id);
                const isSelected = selected.has(r.booking_id);
                return (
                  <tr key={r.booking_id} className="border-b border-warm-grey-100 hover:bg-paper">
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleOne(r.booking_id, r.connect_ready)}
                        disabled={!r.connect_ready}
                      />
                    </td>
                    <td className="p-3">
                      <div className="font-mono text-xs text-warm-grey-700">{r.booking_id.slice(0, 8)}…</div>
                      <div className="text-xs text-warm-grey-700">
                        {fmtDate(r.scheduled_date)} {r.scheduled_time}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="font-medium">{r.expert_name || "—"}</div>
                      <div className="text-xs text-warm-grey-700">{r.expert_email}</div>
                    </td>
                    <td className="p-3">
                      <div className="font-medium">{r.seeker_name || "—"}</div>
                      <div className="text-xs text-warm-grey-700">{r.seeker_email}</div>
                    </td>
                    <td className="p-3 text-right">{fmtUSD(r.amount_total_usd)}</td>
                    <td className="p-3 text-right font-medium">{fmtUSD(r.amount_payout_usd)}</td>
                    <td className="p-3 text-right text-warm-grey-700">{fmtUSD(r.amount_commission_usd)}</td>
                    <td className="p-3">
                      {r.connect_ready ? (
                        <StatusBadge status="pending" />
                      ) : r.blocker === "expert_not_onboarded" ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border bg-warm-grey-100 text-warm-grey-700 border-warm-grey-200">
                          Expert not onboarded
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border bg-amber-100 text-amber-800 border-amber-200">
                          Stripe verifying
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <Button
                        size="sm"
                        onClick={() => releaseOne(r.booking_id)}
                        disabled={!r.connect_ready || isReleasing}
                        className="bg-brand-green hover:bg-brand-green-dark text-white rounded-md"
                      >
                        {isReleasing ? <Loader2 className="w-3 h-3 animate-spin" /> : "Release"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AsyncReviewPayouts rows={data.async_reviews || []} onChanged={load} />
    </div>
  );
}


// ─── Async reviews owed a reviewer payout ──────────────────────────────────
//
// Separate table because the row shape and the release endpoint differ
// from bookings: POST /admin/payouts/async-reviews/{id}/release. The
// backend enforces every guard (paid + delivered, not refunded/disputed,
// reviewer onboarded, not already released, one transfer per payout row);
// the confirm() here is only a fat-finger check.

const ASYNC_BLOCKER_LABEL = {
  expert_not_onboarded: "Reviewer not onboarded",
  stripe_verifying:     "Stripe verifying",
};

function AsyncReviewPayouts({ rows, onChanged }) {
  const [releasing, setReleasing] = useState(new Set());

  const releaseOne = async (r) => {
    const ok = window.confirm(
      `Send ${fmtUSD(r.amount_payout_usd)} to ${r.expert_name || "the reviewer"} ` +
      `for review ${r.async_review_id.slice(0, 8)}? This creates a Stripe transfer.`,
    );
    if (!ok) return;
    setReleasing((prev) => new Set(prev).add(r.async_review_id));
    try {
      await fetchJson(`${API}/admin/payouts/async-reviews/${r.async_review_id}/release`, {
        method: "POST",
        headers: authHeaders(),
      });
      toast.success("Reviewer payout released.");
      await onChanged();
    } catch (e) {
      toast.error(`Release failed: ${e.message}`);
    } finally {
      setReleasing((prev) => {
        const n = new Set(prev);
        n.delete(r.async_review_id);
        return n;
      });
    }
  };

  return (
    <div className="mt-8">
      <div className="font-display text-xl mb-1">Async reviews: {rows.length} pending</div>
      <div className="text-sm text-warm-grey-700 mb-3">
        Paid reviews with a delivered verdict. Reviewer gets 80% of the price; refunds and disputes are checked in Stripe on release.
      </div>
      {rows.length === 0 ? (
        <div className="bg-white border border-warm-grey-200 rounded p-6 text-center text-warm-grey-700">
          No reviewer payouts pending.
        </div>
      ) : (
        <div className="bg-white border border-warm-grey-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-paper border-b border-warm-grey-200">
              <tr>
                <th className="text-left p-3">Review</th>
                <th className="text-left p-3">Reviewer</th>
                <th className="text-left p-3">Client</th>
                <th className="text-right p-3">Price</th>
                <th className="text-right p-3">Payout</th>
                <th className="text-right p-3">Fee</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isReleasing = releasing.has(r.async_review_id);
                return (
                  <tr key={r.async_review_id} className="border-b border-warm-grey-100 hover:bg-paper">
                    <td className="p-3">
                      <div className="font-mono text-xs text-warm-grey-700">{r.async_review_id.slice(0, 8)}…</div>
                      <div className="text-xs text-warm-grey-700">
                        {r.artifact_name || r.category || "—"} · delivered {fmtDate(r.completed_at)}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="font-medium">{r.expert_name || "—"}</div>
                      <div className="text-xs text-warm-grey-700">{r.expert_email}</div>
                    </td>
                    <td className="p-3">
                      <div className="font-medium">{r.client_name || "—"}</div>
                      <div className="text-xs text-warm-grey-700">{r.client_email}</div>
                    </td>
                    <td className="p-3 text-right">{fmtUSD(r.amount_total_usd)}</td>
                    <td className="p-3 text-right font-medium">{fmtUSD(r.amount_payout_usd)}</td>
                    <td className="p-3 text-right text-warm-grey-700">{fmtUSD(r.amount_commission_usd)}</td>
                    <td className="p-3">
                      {!r.connect_ready ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border bg-warm-grey-100 text-warm-grey-700 border-warm-grey-200">
                          {ASYNC_BLOCKER_LABEL[r.blocker] || "Not ready"}
                        </span>
                      ) : r.release_state === "unconfirmed" ? (
                        <span
                          title="A previous release didn't get a Stripe confirmation. Release again is safe: same idempotency key, Stripe checked first."
                          className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border bg-amber-100 text-amber-800 border-amber-200"
                        >
                          Unconfirmed, retry
                        </span>
                      ) : r.release_state === "retry" ? (
                        <span
                          title={r.last_failure_reason || "Previous attempt failed; nothing was sent."}
                          className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border bg-red-100 text-red-800 border-red-200"
                        >
                          Last attempt failed
                        </span>
                      ) : (
                        <StatusBadge status="pending" />
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <Button
                        size="sm"
                        onClick={() => releaseOne(r)}
                        disabled={!r.connect_ready || isReleasing}
                        className="bg-brand-green hover:bg-brand-green-dark text-white rounded-md"
                      >
                        {isReleasing ? <Loader2 className="w-3 h-3 animate-spin" /> : "Release"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ─── Tab: History ──────────────────────────────────────────────────────────


function HistoryTab() {
  const [loading, setLoading] = useState(true);
  const [payouts, setPayouts] = useState([]);
  const [filter, setFilter] = useState("");      // "" | in_transit | paid | failed | reversed
  const [error, setError] = useState(null);
  // Reverse-modal state. `target` = the payout being reversed (null = closed).
  const [reverseTarget, setReverseTarget] = useState(null);
  const [reverseReason, setReverseReason] = useState("");
  const [reverseSubmitting, setReverseSubmitting] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const url = filter
        ? `${API}/admin/payouts/history?status_filter=${encodeURIComponent(filter)}`
        : `${API}/admin/payouts/history`;
      const d = await fetchJson(url, { headers: authHeaders() });
      setPayouts(d.payouts || []);
    } catch (e) {
      setError(e.message || "Failed to load payout history.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // Re-fetch payout history when the filter changes. `load` is
    // component-local; adding it to deps would require useCallback for
    // no behaviour change. (The previous inline disable directive was
    // malformed — `eslint-disable-next-line` only suppresses warnings
    // on the next physical line, not the rest of the same line.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const openReverseModal = (payout) => {
    setReverseTarget(payout);
    setReverseReason("");
  };

  const closeReverseModal = () => {
    if (reverseSubmitting) return;
    setReverseTarget(null);
    setReverseReason("");
  };

  const confirmReverse = async () => {
    if (!reverseTarget) return;
    const trimmed = reverseReason.trim();
    if (trimmed.length < 3) {
      toast.error("Please give a reason (at least 3 characters).");
      return;
    }
    setReverseSubmitting(true);
    try {
      await fetchJson(`${API}/admin/payouts/${reverseTarget.id}/reverse`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ reason: trimmed }),
      });
      toast.success("Payout reversed. Funds clawed back from expert's Stripe account.");
      setReverseTarget(null);
      setReverseReason("");
      await load();
    } catch (e) {
      // Stripe's reversal can fail (e.g. balance_insufficient if the
      // expert already withdrew the funds). Surface Stripe's message
      // so the founder can decide the out-of-band recovery path.
      toast.error(`Reversal failed: ${e.message}`);
    } finally {
      setReverseSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-brand-green" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-red-200 rounded p-6">
        <p className="font-semibold text-red-800">Couldn't load payout history</p>
        <p className="text-sm text-red-700 mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm text-warm-grey-700">Filter:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-warm-grey-300 rounded px-2 py-1 text-sm bg-white"
          >
            <option value="">All statuses</option>
            <option value="in_transit">In transit</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
            <option value="reversed">Reversed</option>
          </select>
          <span className="text-sm text-warm-grey-700">({payouts.length})</span>
        </div>
        <Button onClick={load} variant="ghost" size="sm">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {payouts.length === 0 ? (
        <div className="bg-white border border-warm-grey-200 rounded p-8 text-center text-warm-grey-700">
          No payouts yet.
        </div>
      ) : (
        <div className="bg-white border border-warm-grey-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-paper border-b border-warm-grey-200">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Expert</th>
                <th className="text-right p-3">Amount</th>
                <th className="text-right p-3">Fee</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Booking / review</th>
                <th className="text-left p-3">Stripe Transfer</th>
                <th className="text-right p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id} className="border-b border-warm-grey-100 hover:bg-paper">
                  <td className="p-3 text-warm-grey-700">{fmtDate(p.initiated_at || p.created_at)}</td>
                  <td className="p-3">
                    <div className="font-medium">{p.expert_name || "—"}</div>
                    <div className="text-xs text-warm-grey-700">{p.expert_email}</div>
                  </td>
                  <td className="p-3 text-right font-medium">{fmtUSD(p.amount_usd)}</td>
                  <td className="p-3 text-right text-warm-grey-700">{fmtUSD(p.commission_deducted_usd)}</td>
                  <td className="p-3"><StatusBadge status={p.status} /></td>
                  <td className="p-3 font-mono text-xs text-warm-grey-700">
                    {p.booking_id
                      ? `${p.booking_id.slice(0, 8)}…`
                      : p.async_review_id
                        ? `review ${p.async_review_id.slice(0, 8)}…`
                        : "—"}
                  </td>
                  <td className="p-3 font-mono text-xs">
                    {p.stripe_transfer_id ? (
                      <a
                        href={`https://dashboard.stripe.com/connect/transfers/${p.stripe_transfer_id}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-brand-green hover:underline inline-flex items-center gap-1"
                      >
                        {p.stripe_transfer_id.slice(0, 10)}…
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : "—"}
                  </td>
                  <td className="p-3 text-right">
                    {/* Reversal is only meaningful for in_transit or paid
                        payouts. failed/cancelled never moved money;
                        reversed already happened. */}
                    {(p.status === "in_transit" || p.status === "paid") ? (
                      <button
                        type="button"
                        onClick={() => openReverseModal(p)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border border-warm-grey-300 rounded text-warm-grey-800 hover:bg-warm-grey-50 hover:border-warm-grey-400"
                        title="Reverse this payout (clawback for refund / lost dispute)"
                      >
                        <Undo2 className="w-3 h-3" /> Reverse
                      </button>
                    ) : (
                      <span className="text-xs text-warm-grey-500">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Reverse confirm modal ─────────────────────────────────────── */}
      {reverseTarget && (
        <ReverseConfirmModal
          payout={reverseTarget}
          reason={reverseReason}
          onReasonChange={setReverseReason}
          onConfirm={confirmReverse}
          onCancel={closeReverseModal}
          submitting={reverseSubmitting}
        />
      )}
    </div>
  );
}


// ─── Reverse confirm modal ────────────────────────────────────────────────


function ReverseConfirmModal({ payout, reason, onReasonChange, onConfirm, onCancel, submitting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-warm-grey-900/50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Undo2 className="w-5 h-5 text-warm-grey-700" />
            <h3 className="font-display text-xl">Reverse payout</h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="text-warm-grey-500 hover:text-warm-grey-900 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-warm-grey-800 mb-4 leading-relaxed">
          This will call Stripe's reversal API to claw <strong>{fmtUSD(payout.amount_usd)}</strong>{" "}
          back from <strong>{payout.expert_name || payout.expert_email || "the expert"}</strong>'s
          Stripe Connect account. If the funds have already been paid out to
          their bank, Stripe pulls from the connected account's balance —
          may fail with <code className="text-xs">balance_insufficient</code>{" "}
          if the expert has withdrawn the money.
        </p>

        <label className="block text-sm font-semibold mb-1">
          Reason <span className="text-warm-grey-500 font-normal">(required, audit trail)</span>
        </label>
        <textarea
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          disabled={submitting}
          placeholder="e.g. buyer_refunded, dispute_lost, released_to_wrong_booking"
          rows={3}
          className="w-full border border-warm-grey-300 rounded p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/30 disabled:opacity-50"
        />

        <div className="flex justify-end gap-2 mt-5">
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={submitting || reason.trim().length < 3}
            className="bg-red-600 hover:bg-red-700 text-white rounded-md"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Undo2 className="w-4 h-4 mr-2" />}
            Reverse payout
          </Button>
        </div>
      </div>
    </div>
  );
}


// ─── Page shell ────────────────────────────────────────────────────────────


export default function AdminPayoutsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");

  return (
    <div className="min-h-screen bg-paper">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate("/admin/dashboard")}
          className="-ml-3 mb-2"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to admin
        </Button>

        <div className="flex items-center gap-3 mb-1">
          <Wallet className="w-7 h-7 text-brand-green" />
          <h1 className="font-display text-3xl">Payouts</h1>
        </div>
        <p className="text-warm-grey-700 mb-6 max-w-2xl">
          Bookings the platform owes payouts on, plus past releases. Click Release on a row to fire the Stripe Transfer to the expert's connected account.
        </p>

        <div className="border-b border-warm-grey-200 mb-6">
          <div className="flex gap-1">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  tab === t.id
                    ? "border-brand-green text-brand-green"
                    : "border-transparent text-warm-grey-700 hover:text-warm-grey-900"
                }`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {tab === "pending" && <PendingTab />}
        {tab === "history" && <HistoryTab />}
      </div>
    </div>
  );
}
